import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faCheck, faFireFlameCurved, faFolderOpen, faList, faPlus, faUpDown } from "@fortawesome/free-solid-svg-icons";
import HabitModal from "../habits/HabitModal";
import { createHabit, getHabitsForDate, getHabitsHeatmap, getHabitsSummary, toggleHabit, toYmd } from "../../api/habitsApi";
import "./HabitsCard.css";

const RANGE_OPTIONS = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "1y", label: "Ano" },
];

const PILLAR_WEIGHTS = {
  energia: 25,
  foco: 35,
  clareza: 20,
  saude_mental: 20,
  execucao: 25,
};

const getPillarWeight = (pillar) => PILLAR_WEIGHTS[pillar] || 10;

const getDoneFeedback = (habit) => {
  const messages = {
    energia: "Energia bem cuidada para sustentar o dia.",
    foco: "Foco reforcado. Boa repeticao.",
    clareza: "Clareza em dia. Menos atrito para executar.",
    saude_mental: "Estado mental regulado tambem e performance.",
    execucao: "Execucao registrada. Tracao construida.",
  };
  return messages[habit?.pilar] || "Habito atualizado.";
};

const normalizeSeries = (series = []) =>
  series.map((point, index) => {
    const date = point?.data || point?.date || point?.dia || null;
    const label = date
      ? new Date(date).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })
      : point?.label || `D${index + 1}`;
    return {
      label,
      value: Math.max(0, Math.min(100, Number(point?.percentual ?? point?.percent ?? point?.valor ?? 0))),
    };
  });

const buildLinePoints = (series, width = 390, height = 130) => {
  if (!series.length) return "";
  const step = series.length > 1 ? width / (series.length - 1) : 0;
  return series
    .map((point, index) => `${index * step},${height - (point.value / 100) * height}`)
    .join(" ");
};

const intensityClass = (value) => {
  if (value >= 100) return "full";
  if (value >= 66) return "high";
  if (value >= 33) return "medium";
  if (value > 0) return "low";
  return "empty";
};

const buildMiniHeatmap = (records, selectedDate) => {
  const year = selectedDate.getFullYear();
  const start = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  const first = new Date(start);
  first.setDate(start.getDate() - start.getDay());
  const recordMap = new Map(
    (records || []).map((day) => [String(day?.data || day?.date || "").slice(0, 10), Number(day?.percentual ?? 0)])
  );
  const weeks = [];
  const cursor = new Date(first);

  while (cursor <= end || cursor.getDay() !== 0) {
    const week = [];
    for (let weekday = 0; weekday < 7; weekday += 1) {
      const key = cursor.toISOString().slice(0, 10);
      const inYear = cursor.getFullYear() === year;
      week.push({ key, inYear, value: inYear ? recordMap.get(key) || 0 : null });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

const HabitsCard = ({ selectedDate }) => {
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [habits, setHabits] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [summary, setSummary] = useState({ completedToday: 0, totalToday: 0, currentStreak: 0, consistency: 0, series: [] });
  const [loading, setLoading] = useState(true);
  const [savingHabit, setSavingHabit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadHabits = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [habitRecords, habitSummary] = await Promise.all([
        getHabitsForDate(selectedDate),
        getHabitsSummary(range, selectedDate).catch(() => ({
          completedToday: 0,
          totalToday: 0,
          currentStreak: 0,
          consistency: 0,
          series: [],
        })),
      ]);

      setHabits(habitRecords);
      setSummary((current) => ({
        ...current,
        ...habitSummary,
        totalToday: habitSummary.totalToday || habitRecords.length,
        completedToday: habitSummary.completedToday || habitRecords.filter((habit) => habit.concluido).length,
      }));
    } catch (error) {
      console.error("HabitsCard: erro ao carregar habitos", error);
      setHabits([]);
      setSummary({ completedToday: 0, totalToday: 0, currentStreak: 0, consistency: 0, series: [] });
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar os habitos."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHabits();
  }, [selectedDate, range]);

  useEffect(() => {
    getHabitsHeatmap(selectedDate.getFullYear())
      .then((days) => setHeatmap(days))
      .catch(() => setHeatmap([]));
  }, [selectedDate]);

  const performanceScore = useMemo(() => {
    const totalWeight = habits.reduce((acc, habit) => acc + getPillarWeight(habit.pilar), 0);
    if (!totalWeight) return 0;
    const doneWeight = habits
      .filter((habit) => habit.concluido)
      .reduce((acc, habit) => acc + getPillarWeight(habit.pilar), 0);
    return Math.round((doneWeight / totalWeight) * 100);
  }, [habits]);

  const chartSeries = useMemo(() => normalizeSeries(summary.series || []).slice(-10), [summary.series]);
  const chartPoints = useMemo(() => buildLinePoints(chartSeries), [chartSeries]);
  const miniHeatmap = useMemo(() => buildMiniHeatmap(heatmap, selectedDate), [heatmap, selectedDate]);

  const handleToggle = async (habit) => {
    const nextDone = !habit.concluido;
    try {
      setErrorMessage("");
      setSuccessMessage("");
      await toggleHabit(habit.id, {
        data: toYmd(selectedDate),
        concluido: nextDone,
      });
      await loadHabits();
      setSuccessMessage(nextDone ? getDoneFeedback(habit) : "Ajuste registrado. Retome no proximo bloco.");
    } catch (error) {
      console.error("HabitsCard: erro ao marcar habito", error);
      setErrorMessage("Nao foi possivel atualizar o habito.");
    }
  };

  const handleCreate = async (payload) => {
    try {
      setSavingHabit(true);
      setErrorMessage("");
      setSuccessMessage("");
      await createHabit(payload);
      await loadHabits();
      setShowModal(false);
      setSuccessMessage("Habito salvo e lista atualizada.");
    } catch (error) {
      console.error("HabitsCard: erro ao criar habito", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel criar o habito."
      );
    } finally {
      setSavingHabit(false);
    }
  };

  return (
    <>
      <Card className="habits-card-dashboard h-100 border-0 shadow-sm">
        <Card.Body>
          <div className="habits-card-header">
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon={faFireFlameCurved} /> Habitos
              </h5>
              <small className="text-muted">{summary.completedToday || 0}/{summary.totalToday || habits.length} no dia</small>
            </div>

            <div className="habits-card-actions">
              <Button variant="light" size="sm" className="habits-icon-btn" title="Arquivar" aria-label="Arquivar">
                <FontAwesomeIcon icon={faFolderOpen} />
              </Button>
              <Button variant="light" size="sm" className="habits-icon-btn" title="Ordenar" aria-label="Ordenar">
                <FontAwesomeIcon icon={faUpDown} />
              </Button>
              <Button variant="dark" size="sm" className="habits-icon-btn active" title="Lista" aria-label="Lista">
                <FontAwesomeIcon icon={faList} />
              </Button>
              <Button
                variant="light"
                size="sm"
                className="habits-icon-btn"
                title="Abrir habitos"
                aria-label="Abrir habitos"
                onClick={() => navigate("/habitos")}
              >
                <FontAwesomeIcon icon={faCalendarDays} />
              </Button>
              <Button
                variant="light"
                size="sm"
                className="habits-icon-btn"
                title="Novo habito"
                aria-label="Novo habito"
                onClick={() => setShowModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          </div>

          {errorMessage && <Alert variant="warning" className="mt-3">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success" className="mt-3">{successMessage}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              <div className="habits-list-dashboard">
                {habits.length === 0 ? (
                  <div className="habits-empty-dashboard">
                    Nenhum habito ativo para {toYmd(selectedDate)}.
                  </div>
                ) : (
                  habits.map((habit) => (
                    <label key={habit.id} className="habit-check-row">
                      <input type="checkbox" checked={!!habit.concluido} onChange={() => handleToggle(habit)} />
                      <span className="habit-check-indicator"><FontAwesomeIcon icon={faCheck} /></span>
                      <span>
                        {habit.nome}
                        {habit.habito_ancora && <small>Depois de: {habit.habito_ancora}</small>}
                      </span>
                      <strong className="habit-row-streak">
                        <FontAwesomeIcon icon={faFireFlameCurved} /> {habit.streak_atual || 0}
                      </strong>
                    </label>
                  ))
                )}
              </div>

              <div className="habits-dashboard-divider" />

              <div className="habits-range-tabs">
                {RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={range === option.value ? "active" : ""}
                    onClick={() => setRange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
                <span>{performanceScore}% score</span>
              </div>

              <div className="habits-dashboard-line">
                <div className="habits-dashboard-axis">
                  <span>100%</span>
                  <span>50%</span>
                  <span>0%</span>
                </div>
                <div className="habits-dashboard-plot">
                  <svg viewBox="0 0 390 130" preserveAspectRatio="none" aria-hidden="true">
                    <line x1="0" y1="0" x2="390" y2="0" />
                    <line x1="0" y1="65" x2="390" y2="65" />
                    <line x1="0" y1="130" x2="390" y2="130" />
                    {chartPoints && <polyline points={chartPoints} />}
                    {chartSeries.map((point, index) => {
                      const x = chartSeries.length > 1 ? (index * 390) / (chartSeries.length - 1) : 0;
                      const y = 130 - (point.value / 100) * 130;
                      return <circle key={`${point.label}-${index}`} cx={x} cy={y} r="4" />;
                    })}
                  </svg>
                  <div className="habits-dashboard-labels">
                    {chartSeries.map((point, index) => <span key={`${point.label}-${index}`}>{point.label}</span>)}
                  </div>
                </div>
              </div>

              <div className="habits-dashboard-divider" />

              <div className="habits-dashboard-heatmap-head">
                <strong>Consistencia {selectedDate.getFullYear()}</strong>
                <span>{summary.currentStreak || 0} dias de sequencia</span>
              </div>
              <div className="habits-dashboard-heatmap">
                <div className="habits-dashboard-weekdays">
                  <span>Dom</span><span>Seg</span><span>Ter</span><span>Qua</span><span>Qui</span><span>Sex</span><span>Sab</span>
                </div>
                <div className="habits-dashboard-heatmap-scroll">
                  <div className="habits-dashboard-weeks">
                    {miniHeatmap.map((week, weekIndex) => (
                      <div key={`mini-week-${weekIndex}`} className="habits-dashboard-week">
                        {week.map((day) => (
                          <div
                            key={day.key}
                            className={`habits-dashboard-cell ${day.inYear ? intensityClass(day.value) : "outside"}`}
                            title={day.inYear ? `${day.key} - ${Math.round(day.value || 0)}%` : ""}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <HabitModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleCreate}
        saving={savingHabit}
      />
    </>
  );
};

export default HabitsCard;
